import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, ResponsiveContainer, LabelList } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, fmtMonthShort, slugify, pctChange, getFYStart, getFYPlainLabel, fetchFortnightFYByMill, fetchLatestFortnightBreakdown, formatFortnightRange, RANK_DISCLOSURE } from '../utils';
import { generateDistrictPDF } from '../shareExport';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedFactoryName, localizedStateName, localizedDistrictName } from '../../../utils/localizedDisplayName';

const ShareIcon = ({ size = 16, color = '#0F2238' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
  </svg>
);

const formatCompactMT = (v) => {
  const n = Number(v);
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

// Labels every bar up to 10 months; beyond that, alternates to avoid clutter.
const renderTrendLabel = (props, totalBars) => {
  const { x, y, width, value, index } = props;
  if (totalBars > 10 && index % 2 !== 0) return null;
  return (
    <text x={x + width / 2} y={y - 4} textAnchor="middle" fontSize={8} fill="#78716C" fontWeight={600}>
      {formatCompactMT(value)}
    </text>
  );
};

const DistrictPage = () => {
  const { t, language } = useTranslation();
  const { districtSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snap, setSnap] = useState(null);
  const [showAllMills, setShowAllMills] = useState(false);
  const [sharing, setSharing] = useState(false);
  const trendChartRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Latest month
        const { data: natLatest, error: nErr } = await supabase
          .from('mi_v_national_monthly')
          .select('period_month, total_allocated_mt')
          .order('period_month', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (nErr) throw nErr;
        if (!natLatest) throw new Error('No data available');
        const latestMonth = natLatest.period_month;

        // 2. All districts for latest month -> resolve slug + compute ranks
        const { data: allDistricts, error: adErr } = await supabase
          .from('mi_v_district_monthly')
          .select('canonical_state, canonical_district, total_allocated_mt, mills_active, mills_reporting')
          .eq('period_month', latestMonth);
        if (adErr) throw adErr;

        const me = (allDistricts || []).find((d) => slugify(d.canonical_district) === districtSlug);
        if (!me) throw new Error('District not found');
        const districtName = me.canonical_district;
        const stateName = me.canonical_state;

        const sortedNat = [...allDistricts].sort((a, b) => Number(b.total_allocated_mt) - Number(a.total_allocated_mt));
        const natRank = sortedNat.findIndex((d) => d.canonical_district === districtName && d.canonical_state === stateName) + 1;
        const stateDistricts = sortedNat.filter((d) => d.canonical_state === stateName);
        const stateRank = stateDistricts.findIndex((d) => d.canonical_district === districtName) + 1;

        // 3. Full monthly history for this district
        const { data: history, error: hErr } = await supabase
          .from('mi_v_district_monthly')
          .select('period_month, total_allocated_mt')
          .eq('canonical_district', districtName)
          .eq('canonical_state', stateName)
          .order('period_month', { ascending: true });
        if (hErr) throw hErr;

        const hist = history || [];
        const latestRow = hist[hist.length - 1];
        const prevRow = hist[hist.length - 2];
        const mom = prevRow ? pctChange(latestRow.total_allocated_mt, prevRow.total_allocated_mt) : null;
        const lastYearMonth = new Date(latestMonth);
        lastYearMonth.setFullYear(lastYearMonth.getFullYear() - 1);
        const lyStr = lastYearMonth.toISOString().slice(0, 10);
        const lyRow = hist.find((r) => r.period_month === lyStr);
        const yoy = lyRow ? pctChange(latestRow.total_allocated_mt, lyRow.total_allocated_mt) : null;

        const fyStart = getFYStart(latestMonth);
        const fyTotal = hist.filter((r) => r.period_month >= fyStart && r.period_month <= latestMonth)
          .reduce((s, r) => s + Number(r.total_allocated_mt), 0);
        const lifetime = hist.reduce((s, r) => s + Number(r.total_allocated_mt), 0);

        // 4. State + national share
        const { data: stateRow } = await supabase
          .from('mi_v_state_monthly')
          .select('total_allocated_mt')
          .eq('canonical_state', stateName)
          .eq('period_month', latestMonth)
          .maybeSingle();
        const stateShare = stateRow && Number(stateRow.total_allocated_mt) > 0
          ? (Number(me.total_allocated_mt) / Number(stateRow.total_allocated_mt)) * 100 : null;
        const natShare = Number(natLatest.total_allocated_mt) > 0
          ? (Number(me.total_allocated_mt) / Number(natLatest.total_allocated_mt)) * 100 : null;

        // 5. Mills in this district
        const { data: mills, error: mErr } = await supabase
          .from('mi_v_mill_metrics')
          .select('mill_id, canonical_mill_name, latest_month, latest_qty_mt, max_qty_mt, max_qty_month, months_reporting')
          .eq('canonical_district', districtName)
          .eq('canonical_state', stateName);
        if (mErr) throw mErr;
        const millRows = (mills || []).map((m) => ({
          ...m,
          currentMT: m.latest_month === latestMonth ? Number(m.latest_qty_mt) : 0,
        })).sort((a, b) => b.currentMT - a.currentMT || Number(b.max_qty_mt) - Number(a.max_qty_mt));

        // Top up the FY cumulative with this district's share of any fortnightly
        // release not yet folded into a combined mi_release_orders row (see
        // fetchFortnightFYByMill) - restricted to mills already known to belong
        // to this district via millRows above.
        const fortnightFY = await fetchFortnightFYByMill(latestMonth);
        const fortnightFYTotal = millRows.reduce((s, m) => s + (fortnightFY.get(m.mill_id) || 0), 0);
        const fyTotalWithFortnight = fyTotal + fortnightFYTotal;

        // Explicit "Sep 1-15, 2026" figure for this district (not folded into
        // anything above) - sum of the latest active fortnight's per-mill lines
        // for mills already known to belong to this district via millRows.
        const fortnightBreakdown = await fetchLatestFortnightBreakdown();
        const fortnightTotal = fortnightBreakdown
          ? millRows.reduce((s, m) => s + (fortnightBreakdown.byMill.get(m.mill_id) || 0), 0)
          : null;

        // The leaderboard displays the fortnight MT per mill (below) once one's
        // active, not currentMT - re-sort so the on-screen order matches what's
        // actually shown, same fix as the other MI browse pages.
        if (fortnightBreakdown) {
          millRows.sort((a, b) => (fortnightBreakdown.byMill.get(b.mill_id) ?? 0) - (fortnightBreakdown.byMill.get(a.mill_id) ?? 0));
        }

        // 6. Latest news for this district (headline + link only, no source shown)
        const { data: newsRows } = await supabase
          .from('mi_district_news')
          .select('title, url, published_at, language')
          .eq('canonical_district', districtName)
          .eq('canonical_state', stateName)
          .order('published_at', { ascending: false, nullsFirst: false })
          .limit(3);

        if (!isMounted) return;
        setSnap({
          districtName, stateName, latestMonth,
          latestTotal: Number(me.total_allocated_mt),
          millsActive: me.mills_active, millsReporting: me.mills_reporting,
          mom, yoy, fyTotal: fyTotalWithFortnight, fyLabel: getFYPlainLabel(latestMonth, t), lifetime,
          fortnightTotal, fortnightBreakdown,
          firstMonth: hist[0]?.period_month,
          natRank, natTotal: sortedNat.length, stateRank, stateTotal: stateDistricts.length,
          stateShare, natShare,
          trend: hist.slice(-24).map((r) => ({ month: fmtMonthShort(r.period_month), mt: Number(r.total_allocated_mt) })),
          mills: millRows,
          news: newsRows || [],
        });
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [districtSlug]);

  const handleSharePDF = async () => {
    setSharing(true);
    await generateDistrictPDF({ snap, chartRef: trendChartRef, action: 'share' });
    setSharing(false);
  };

  const badge = (val) => {
    if (val === null) return { text: t('marketIntelligence.notAvailable', 'N/A'), bg: '#F3F4F6', color: '#6B7280' };
    const rounded = Math.round(val * 10) / 10;
    if (Math.abs(rounded) < 0.05) return { text: t('marketIntelligence.flatTrend', 'flat'), bg: '#F3F4F6', color: '#6B7280' };
    return rounded > 0
      ? { text: `\u2191 ${rounded}%`, bg: '#E7F7EF', color: '#15803D' }
      : { text: `\u2193 ${Math.abs(rounded)}%`, bg: '#FDECEC', color: '#B91C1C' };
  };

  const shownMills = snap ? (showAllMills ? snap.mills : snap.mills.slice(0, 8)) : [];

  return (
    <div className="min-h-screen" style={{ background: '#0F2238' }}>
      <Helmet>
        <title>{snap ? `${snap.districtName}, ${snap.stateName} — Sugar Quota Data | SugarHathi` : 'District Sugar Quota Data | SugarHathi Market Intelligence'}</title>
        <meta name="description" content={snap ? `Live sugar quota release-order data for ${snap.districtName}, ${snap.stateName}. Monthly mill-wise allocation figures, verified by SugarHathi.` : 'Live district-wise sugar quota release-order data across India, verified by SugarHathi.'} />
      </Helmet>
      <MIHeader subtitle={snap ? `${localizedDistrictName(snap.districtName, snap.stateName, language)} district` : 'District'} />
      <main className="min-h-[calc(100vh-64px)]" style={{ background: '#FDF8F0' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24" style={{ color: '#78716C' }}>
              <Icon name="Loader" size={32} className="animate-spin mb-3" /><p>{t('common.loading', 'Loading...')}</p>
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24" style={{ color: '#78716C' }}>
              <Icon name="AlertTriangle" size={32} className="mb-3" style={{ color: '#DC2626' }} /><p>{error}</p>
            </div>
          )}
          {!loading && !error && snap && (
            <>
              {/* Breadcrumb escape - top */}
              <div className="flex items-center gap-1.5 flex-wrap mb-4 text-xs font-medium" style={{ color: '#A8A29E' }}>
                <button onClick={() => navigate('/market-intelligence')} className="hover:underline" style={{ color: '#B45309' }}>{t('marketIntelligence.quotaTrackerNav', 'Quota Tracker')}</button>
                <span>/</span>
                <button onClick={() => navigate('/market-intelligence/districts')} className="hover:underline" style={{ color: '#B45309' }}>{t('marketIntelligence.districtsNav', 'Districts')}</button>
                <span>/</span>
                <span style={{ color: '#57534E' }}>{localizedDistrictName(snap.districtName, snap.stateName, language)}</span>
              </div>

              {/* Hero band */}
              <div className="rounded-[18px] p-5 mb-4 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0F2238 0%, #1E3A5F 55%, #B45309 130%)' }}>
                <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full" style={{ background: 'rgba(245,158,11,0.15)' }} />
                <div className="absolute -right-2 top-10 w-16 h-16 rounded-full" style={{ background: 'rgba(245,158,11,0.12)' }} />
                <div className="flex items-start justify-between gap-3 relative">
                  <div>
                    <p className="text-xs font-bold tracking-widest mb-1" style={{ color: '#FBBF24' }}>{t('marketIntelligence.districtSnapshot', 'DISTRICT SNAPSHOT')}</p>
                    <h1 className="text-2xl font-black text-white leading-tight">{localizedDistrictName(snap.districtName, snap.stateName, language)}</h1>
                    <p className="text-sm font-medium" style={{ color: '#93C5FD' }}>{localizedStateName(snap.stateName, language)}</p>
                  </div>
                  <button onClick={handleSharePDF} disabled={sharing}
                    className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[10px] disabled:opacity-50"
                    style={{ background: '#F59E0B', color: '#0F2238' }}>
                    <ShareIcon size={14} color="#0F2238" /> {sharing ? t('marketIntelligence.preparing', 'Preparing...') : t('marketIntelligence.sharePdf', 'Share PDF')}
                  </button>
                </div>
                <div className="mt-4 relative">
                  <p className="text-xs mb-0.5" style={{ color: '#D8DEE8' }}>
                    {snap.fortnightBreakdown
                      ? `${formatFortnightRange(snap.fortnightBreakdown.periodStart, snap.fortnightBreakdown.periodEnd, language) || snap.fortnightBreakdown.periodLabel} allocation`
                      : `${fmtMonth(snap.latestMonth)} allocation`}
                  </p>
                  <div className="flex items-end gap-2 flex-wrap">
                    <p className="text-4xl font-black" style={{ color: '#F59E0B' }}>
                      {fmtNum(snap.fortnightBreakdown ? snap.fortnightTotal : snap.latestTotal)} <span className="text-lg">MT</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Rank chips */}
              <div className="grid grid-cols-2 gap-3 mb-1">
                <div className="rounded-[14px] p-3.5 text-center" style={{ background: '#EEF0FE', border: '1px solid #D2D6FB' }}>
                  <p className="text-[11px] font-semibold mb-0.5" style={{ color: '#4338CA' }}>{t('marketIntelligence.inState', 'In {{state}}', { state: localizedStateName(snap.stateName, language) })}</p>
                  <p className="text-xl font-black" style={{ color: '#0F2238' }}>#{snap.stateRank} <span className="text-xs font-semibold" style={{ color: '#78716C' }}>{t('marketIntelligence.ofCount', 'of {{count}}', { count: snap.stateTotal })}</span></p>
                </div>
                <div className="rounded-[14px] p-3.5 text-center" style={{ background: '#FFF3E0', border: '1px solid #FDE1B8' }}>
                  <p className="text-[11px] font-semibold mb-0.5" style={{ color: '#B45309' }}>{t('marketIntelligence.nationally', 'Nationally')}</p>
                  <p className="text-xl font-black" style={{ color: '#0F2238' }}>#{snap.natRank} <span className="text-xs font-semibold" style={{ color: '#78716C' }}>{t('marketIntelligence.ofCount', 'of {{count}}', { count: snap.natTotal })}</span></p>
                </div>
              </div>
              <p className="text-xs mb-5" style={{ color: '#A8A29E' }}>{RANK_DISCLOSURE(fmtMonthShort(snap.latestMonth), t)}</p>

              {/* Stat cards */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="rounded-[14px] p-4" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#15803D' }}>{t('marketIntelligence.millsActive', 'Mills active')}</p>
                  <p className="text-lg font-black" style={{ color: '#15803D' }}>{snap.millsActive} <span className="text-xs font-medium" style={{ color: '#78716C' }}>{t('marketIntelligence.ofCount', 'of {{count}}', { count: snap.millsReporting })}</span></p>
                </div>
                <div className="rounded-[14px] p-4" style={{ background: '#FFF3E0', border: '1px solid #FDE1B8' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#B45309' }}>{snap.fyLabel}</p>
                  <p className="text-lg font-black" style={{ color: '#0F2238' }}>{fmtNum(snap.fyTotal)} MT</p>
                </div>
                {snap.fortnightBreakdown ? (
                  <div className="rounded-[14px] p-4" style={{ background: '#FEF3F2', border: '1px solid #FBD5D5' }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#B42318' }}>
                      {t('marketIntelligence.fortnightColumnLabel', { range: formatFortnightRange(snap.fortnightBreakdown.periodStart, snap.fortnightBreakdown.periodEnd, language) || snap.fortnightBreakdown.periodLabel, defaultValue: '{{range}} (MT)' })}
                    </p>
                    <p className="text-lg font-black" style={{ color: '#0F2238' }}>{snap.fortnightTotal !== null ? fmtNum(snap.fortnightTotal) : 'N/A'} MT</p>
                  </div>
                ) : null}
                <div className={snap.fortnightBreakdown ? 'rounded-[14px] p-4' : 'col-span-2 rounded-[14px] p-4'} style={{ background: '#EEF4FF', border: '1px solid #BFDBFE' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#1D4ED8' }}>{t('marketIntelligence.shareThisMonth', 'Share this month')}</p>
                  <p className="text-sm font-black" style={{ color: '#0F2238' }}>
                    {snap.stateShare !== null ? t('marketIntelligence.percentOfState', '{{pct}}% of state', { pct: snap.stateShare.toFixed(1) }) : t('marketIntelligence.notAvailable', 'N/A')}
                  </p>
                  <p className="text-xs font-semibold" style={{ color: '#78716C' }}>
                    {snap.natShare !== null ? t('marketIntelligence.percentOfIndia', '{{pct}}% of India', { pct: snap.natShare.toFixed(2) }) : ''}
                  </p>
                </div>
              </div>

              {/* Trend */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>{t('marketIntelligence.lastNMonths', 'Last {{count}} months', { count: snap.trend.length })}</p>
              <div className="h-44 mb-6 rounded-[10px] p-2" style={{ background: '#FFFFFF', border: '1px solid #E7E0D3' }} ref={trendChartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={snap.trend} margin={{ top: 18 }}>
                    <XAxis dataKey="month" stroke="#A8A29E" fontSize={9} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(snap.trend.length / 8))} />
                    <Bar dataKey="mt" fill="#F59E0B" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="mt" content={(p) => renderTrendLabel(p, snap.trend.length)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Mills leaderboard */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>{t('marketIntelligence.factoriesInDistrict', 'Factories in {{district}} ({{count}})', { district: localizedDistrictName(snap.districtName, snap.stateName, language), count: snap.mills.length })}</p>
              <div className="rounded-[10px] overflow-hidden mb-2" style={{ border: '1px solid #E7E0D3' }}>
                <div className={`grid ${snap.fortnightBreakdown ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-[auto_1fr]'} gap-2 px-3 py-2`} style={{ background: '#F5EDDD' }}>
                  <span className="text-xs font-semibold w-5" style={{ color: '#78716C' }}>#</span>
                  <span className="text-xs font-semibold" style={{ color: '#78716C' }}>{t('common.factory', 'Factory')}</span>
                  {snap.fortnightBreakdown && (
                    <span className="text-xs font-semibold text-right" style={{ color: '#78716C', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '80px' }}>{formatFortnightRange(snap.fortnightBreakdown.periodStart, snap.fortnightBreakdown.periodEnd, language) || snap.fortnightBreakdown.periodLabel} MT</span>
                  )}
                </div>
                {shownMills.map((m, i) => {
                  const fortnightMt = snap.fortnightBreakdown ? (snap.fortnightBreakdown.byMill.get(m.mill_id) ?? 0) : null;
                  const isTop = i === 0 && (snap.fortnightBreakdown ? fortnightMt > 0 : m.currentMT > 0);
                  return (
                    <button key={m.mill_id}
                      onClick={() => navigate(`/market-intelligence/factory/${slugify(m.canonical_mill_name)}`)}
                      className={`w-full grid ${snap.fortnightBreakdown ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-[auto_1fr]'} gap-2 px-3 py-2.5 text-left items-start hover:opacity-80 transition-opacity`}
                      style={{ background: isTop ? '#FFF3E0' : i % 2 === 0 ? '#FFFFFF' : '#FDF6E9', borderTop: '1px solid #F5EDDD' }}>
                      <span className="text-sm font-bold w-5" style={{ color: isTop ? '#B45309' : '#A8A29E' }}>
                        {isTop ? <Icon name="Crown" size={15} color="#B45309" /> : i + 1}
                      </span>
                      <span className="text-sm pr-2 underline" style={{ color: '#2563EB', lineHeight: 1.4 }}>{localizedFactoryName(m, language, 'canonical_mill_name')}</span>
                      {snap.fortnightBreakdown && (
                        <span className="text-sm font-bold whitespace-nowrap" style={{ color: fortnightMt > 0 ? '#B42318' : '#A8A29E' }}>{fmtNum(fortnightMt)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {snap.mills.length > 8 && (
                <button onClick={() => setShowAllMills(!showAllMills)}
                  className="w-full mb-6 text-sm font-bold rounded-[10px] py-2"
                  style={{ border: '2px solid #F59E0B', color: '#B45309', background: 'transparent' }}>
                  {showAllMills ? t('marketIntelligence.showLess', 'Show less') : t('marketIntelligence.showAllFactories', 'Show all {{count}} factories', { count: snap.mills.length })}
                </button>
              )}
              {snap.mills.length <= 8 && <div className="mb-4" />}

              {/* Latest news */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>{t('marketIntelligence.latestNews', 'Latest news')}</p>
              <div className="rounded-[14px] p-4 mb-6" style={{ background: '#FFFFFF', border: '1px solid #E7E0D3' }}>
                {snap.news.length === 0 ? (
                  <p className="text-sm" style={{ color: '#A8A29E' }}>{t('marketIntelligence.noLatestArticles', 'No latest articles')}</p>
                ) : (
                  <div className="space-y-3">
                    {snap.news.map((n, i) => (
                      <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-start gap-2 group">
                        <Icon name="ExternalLink" size={13} color="#B45309" className="flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="text-sm truncate block group-hover:underline" style={{ color: '#292524' }}>{n.title}</span>
                          {n.published_at && (
                            <p className="text-xs mt-0.5" style={{ color: '#A8A29E' }}>{fmtMonth(n.published_at)}</p>
                          )}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* SugarHathi summary */}
              <div className="rounded-[14px] p-5 mb-6" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(180deg, #F59E0B 0%, #D97706 100%)' }}><Icon name="Zap" size={14} color="#FFFFFF" /></div>
                  <span className="text-sm font-bold" style={{ color: '#0F2238' }}>{t('mandiFactory.summaryTitle', 'SugarHathi summary')}</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#365314' }}>
                  {snap.stateShare !== null
                    ? t('marketIntelligence.summaryReceivedWithShare', '{{district}} received {{total}} MT in {{month}} across {{millsActive}} active mills, {{share}}% of {{state}}\'s quota.', { district: localizedDistrictName(snap.districtName, snap.stateName, language), total: fmtNum(snap.latestTotal), month: fmtMonth(snap.latestMonth), millsActive: snap.millsActive, share: snap.stateShare.toFixed(1), state: localizedStateName(snap.stateName, language) })
                    : t('marketIntelligence.summaryReceivedNoShare', '{{district}} received {{total}} MT in {{month}} across {{millsActive}} active mills.', { district: localizedDistrictName(snap.districtName, snap.stateName, language), total: fmtNum(snap.latestTotal), month: fmtMonth(snap.latestMonth), millsActive: snap.millsActive })}
                  {' '}{t('marketIntelligence.summaryDistrictRank', 'Ranks #{{stateRank}} of {{stateTotal}} districts in {{state}} and #{{natRank}} of {{natTotal}} nationally.', { stateRank: snap.stateRank, stateTotal: snap.stateTotal, state: localizedStateName(snap.stateName, language), natRank: snap.natRank, natTotal: snap.natTotal })}
                  {' '}{t('marketIntelligence.summaryFyTotal', '{{fyLabel}}: {{fyTotal}} MT.', { fyLabel: snap.fyLabel, fyTotal: fmtNum(snap.fyTotal) })}
                </p>
              </div>

              {/* Escape tiles */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>{t('marketIntelligence.keepExploring', 'Keep exploring')}</p>
              <div className="grid grid-cols-2 gap-3 mb-8">
                <button onClick={() => navigate('/market-intelligence')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#0F2238' }}>
                  <Icon name="Home" size={18} color="#F59E0B" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.quotaTrackerHome', 'Quota Tracker home')}</p>
                </button>
                <button onClick={() => navigate('/market-intelligence/districts')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#4338CA' }}>
                  <Icon name="Map" size={18} color="#FFFFFF" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.allDistricts', 'All districts')}</p>
                </button>
                <button onClick={() => navigate(`/market-intelligence/factories?district=${slugify(snap.districtName)}`)} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#B45309' }}>
                  <Icon name="Factory" size={18} color="#FFFFFF" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.factoriesHere', 'Factories here')}</p>
                </button>
                <button onClick={() => navigate('/market-intelligence/compare')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#15803D' }}>
                  <Icon name="GitCompare" size={18} color="#FFFFFF" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.compareFactories', 'Compare factories')}</p>
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default DistrictPage;



